import type {
  FetchOptions,
  ApiResponse,
  ApiError,
  ApiEnvelope,
  ApiMetadata,
  RequestBody,
} from '@/shared/model/types/http';
import {
  ACCESS_TOKEN_COOKIE_NAME,
  COOKIE_MAX_AGE,
} from '@/shared/constants/cookiesConstants';
import { isRecord } from '@/shared/utils/isRecord';
import { isApiErrorBody } from '@/shared/utils/isApiError';

const getErrorResponse = (error: unknown): ApiError['response'] | undefined => {
  if (!isRecord(error) || !isRecord(error.response)) return undefined;
  const { status, _data: data } = error.response;
  return {
    status: typeof status === 'number' ? status : undefined,
    data: isApiErrorBody(data) ? data : undefined,
  };
};

const emptyApiPayload = <T>(): T => JSON.parse('{}');

const isApiEnvelope = <T, M>(
  payload: T | ApiEnvelope<T, M>,
): payload is ApiEnvelope<T, M> =>
  isRecord(payload) && Object.prototype.hasOwnProperty.call(payload, 'data');

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1] ?? '') : null;
};

export const useApi = () => {
  const nuxtApp = useNuxtApp();
  const config = useRuntimeConfig();
  const baseURL = config.public.apiUrl;
  const event = useRequestEvent();
  const isServer = Boolean(event);
  const userAgent = isServer ? String(event?.node.req.headers['user-agent'] ?? '') : '';
  const ssrToken = useCookie(ACCESS_TOKEN_COOKIE_NAME, {
    path: '/',
    maxAge: COOKIE_MAX_AGE,
  });

  const isSessionAuthorized = (): boolean =>
    Boolean(nuxtApp.$pinia?.state.value.user?.isAuth);

  const fetchHttp = async <T, M = ApiMetadata>(
    url: string,
    options: FetchOptions = {},
  ): Promise<ApiResponse<T, M>> => {
    try {
      const headers: Record<string, string> = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Request-Context': isServer ? 'server' : 'client',
        ...options.headers,
      };

      if (isServer && userAgent) {
        headers['User-Agent'] = userAgent;
      }

      const hasExplicitAuth = Boolean(headers.Authorization);

      const tokenValue = isServer ? ssrToken.value : readCookie(ACCESS_TOKEN_COOKIE_NAME);

      if (tokenValue && !hasExplicitAuth && isSessionAuthorized()) {
        headers.Authorization = `Bearer ${tokenValue}`;
      }

      if (!tokenValue && !hasExplicitAuth && headers.Authorization) {
        delete headers.Authorization;
      }

      let finalUrl = `${String(baseURL).replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;

      if (options.params) {
        const queryParams = new URLSearchParams();

        Object.entries(options.params).forEach(([key, value]) => {
          if (value == null) return;

          if (Array.isArray(value)) {
            value.forEach((v) => queryParams.append(key, String(v)));
          } else {
            queryParams.append(key, String(value));
          }
        });

        const queryString = queryParams.toString();
        if (queryString) {
          finalUrl += `?${queryString}`;
        }
      }

      const response = await $fetch.raw<T | ApiEnvelope<T, M>, string>(finalUrl, {
        method: options.method || 'GET',
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
      });

      const { status } = response;

      const payload: T | ApiEnvelope<T, M> =
        (status === 204 ? undefined : response._data) ?? emptyApiPayload<T>();

      if (!isApiEnvelope(payload)) {
        return { status, data: payload };
      }

      const result: ApiResponse<T, M> = { status, data: payload.data };

      if (payload.meta) {
        result.meta = payload.meta;
      }
      if (payload.metadata) {
        result.metadata = payload.metadata;
      }

      return result;
    } catch (error: unknown) {
      const response = getErrorResponse(error);
      const status = response?.status || 500;

      const apiError: ApiError = {
        status,
        response: response || {},
        cause: error,
      };

      throw apiError;
    }
  };

  return {
    get: <T, M = ApiMetadata>(url: string, options?: FetchOptions) =>
      fetchHttp<T, M>(url, { method: 'GET', ...options }),
    post: <T, M = ApiMetadata>(url: string, body?: RequestBody, options?: FetchOptions) =>
      fetchHttp<T, M>(url, { method: 'POST', body, ...options }),
    put: <T, M = ApiMetadata>(url: string, body: RequestBody, options?: FetchOptions) =>
      fetchHttp<T, M>(url, { method: 'PUT', body, ...options }),
    patch: <T, M = ApiMetadata>(url: string, body: RequestBody, options?: FetchOptions) =>
      fetchHttp<T, M>(url, { method: 'PATCH', body, ...options }),
    delete: <T, M = ApiMetadata>(url: string, options?: FetchOptions) =>
      fetchHttp<T, M>(url, { method: 'DELETE', ...options }),
  };
};
